import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trophy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { trackAnswerFeedback } from '@/lib/analytics';

export function AnswerFeedback() {
  const navigate = useNavigate();
  const [voted, setVoted] = useState<'yes' | 'no' | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [pendingVote, setPendingVote] = useState<'yes' | 'no' | null>(null);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);

  function handleVote(helpful: boolean) {
    if (voted) return;
    setPendingVote(helpful ? 'yes' : 'no');
    setShowPopup(true);
  }

  function handleSubmit() {
    if (!pendingVote) return;
    setVoted(pendingVote);
    trackAnswerFeedback(pendingVote === 'yes');
    setShowPopup(false);
    setSubmitted(true);
  }

  function handleCancel() {
    setShowPopup(false);
    setPendingVote(null);
    setComment('');
  }

  const promptText =
    pendingVote === 'yes'
      ? 'What helped most about this answer?'
      : 'What felt off, incomplete, or what would you have done differently?';

  return (
    <>
      {/* ── Feedback Popup Modal ─────────────────────────────────────────── */}
      <AnimatePresence>
        {showPopup && (
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center px-4 pb-6 sm:items-center"
            style={{ background: 'hsl(0 0% 0% / 0.55)', backdropFilter: 'blur(4px)' }}
            onClick={handleCancel}
          >
            <motion.div
              key="popup"
              initial={{ opacity: 0, y: 28, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.96 }}
              transition={{ duration: 0.28, ease: 'easeOut' }}
              onClick={e => e.stopPropagation()}
              className="w-full max-w-sm rounded-2xl flex flex-col gap-4 p-6"
              style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
            >
              <AnimatePresence mode="wait">
                {!submitted ? (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    className="flex flex-col gap-4"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{pendingVote === 'yes' ? '👍' : '👎'}</span>
                        <p className="font-body text-sm font-semibold text-foreground leading-snug">
                          {promptText}
                        </p>
                      </div>
                      <button
                        onClick={handleCancel}
                        className="shrink-0 rounded-lg p-1 text-muted-foreground transition-colors hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Text input */}
                    <textarea
                      value={comment}
                      onChange={e => setComment(e.target.value)}
                      placeholder="Share your thoughts here…"
                      rows={3}
                      className="w-full resize-none rounded-xl px-4 py-3 font-body text-sm outline-none transition-all"
                      style={{
                        background: 'hsl(var(--muted))',
                        border: '1px solid hsl(var(--border))',
                        color: 'hsl(var(--foreground))',
                      }}
                      onFocus={e => (e.currentTarget.style.borderColor = 'hsl(var(--primary))')}
                      onBlur={e => (e.currentTarget.style.borderColor = 'hsl(var(--border))')}
                    />

                    {/* Buttons */}
                    <div className="flex items-center gap-2">
                      <motion.button
                        whileHover={{ y: -1, filter: 'brightness(1.08)' }}
                        whileTap={{ scale: 0.97 }}
                        onClick={handleSubmit}
                        className="flex-1 rounded-xl py-2.5 font-body text-sm font-semibold"
                        style={{ background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }}
                      >
                        Submit Feedback
                      </motion.button>
                      <button
                        onClick={handleCancel}
                        className="rounded-xl border border-border px-4 py-2.5 font-body text-sm text-muted-foreground transition-colors hover:text-foreground"
                      >
                        Cancel
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="thankyou"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className="flex flex-col gap-5"
                  >
                    {/* Close */}
                    <div className="flex justify-end">
                      <button
                        onClick={handleCancel}
                        className="rounded-lg p-1 text-muted-foreground transition-colors hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Confirmation */}
                    <div className="flex flex-col items-center gap-1 text-center">
                      <p className="font-body text-sm font-semibold text-foreground">
                        Thank you for your feedback.
                      </p>
                      <p className="font-body text-xs text-muted-foreground">
                        Your input helps improve HPE answers over time.
                      </p>
                    </div>

                    {/* Divider */}
                    <div className="h-px w-full bg-border" />

                    {/* Competition bridge */}
                    <div
                      className="flex flex-col items-center gap-3 rounded-xl px-4 py-4 text-center"
                      style={{
                        background: 'hsl(44 35% 10%)',
                        border: '1px solid hsl(44 50% 26%)',
                      }}
                    >
                      <p className="font-display text-base leading-snug" style={{ color: 'hsl(44 70% 68%)' }}>
                        Have a better method?
                      </p>
                      <p className="font-body text-xs leading-relaxed" style={{ color: 'hsl(44 30% 52%)' }}>
                        Turn it into a real transformation and win cash in the competition.
                      </p>
                      <motion.button
                        whileHover={{ y: -2, filter: 'brightness(1.1)' }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => { handleCancel(); navigate('/fix-it'); }}
                        className="flex items-center gap-2 rounded-xl px-5 py-2.5 font-body text-sm font-semibold"
                        style={{
                          background: 'hsl(44 65% 52%)',
                          color: 'hsl(38 28% 8%)',
                          boxShadow: '0 4px 18px -4px hsl(44 65% 50% / 0.45)',
                        }}
                      >
                        <Trophy className="h-4 w-4" />
                        Enter Competition
                      </motion.button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Inline Feedback Card ─────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45, duration: 0.4 }}
        className="mt-2 rounded-2xl border border-border bg-card px-5 py-5 flex flex-col items-center gap-4 text-center"
      >
        {/* Feedback question */}
        <div className="space-y-3 w-full">
          <p className="font-body text-sm font-medium text-foreground">Was this answer helpful?</p>

          <AnimatePresence mode="wait">
            {!voted ? (
              <motion.div
                key="buttons"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="flex items-center justify-center gap-3"
              >
                <motion.button
                  onClick={() => handleVote(true)}
                  whileHover={{ y: -2, scale: 1.08 }}
                  whileTap={{ scale: 0.94 }}
                  className="flex items-center gap-2 rounded-xl border border-border bg-background px-5 py-2.5 font-body text-sm font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-accent/20"
                >
                  <span className="text-base">👍</span>
                  <span>Yes</span>
                </motion.button>

                <motion.button
                  onClick={() => handleVote(false)}
                  whileHover={{ y: -2, scale: 1.08 }}
                  whileTap={{ scale: 0.94 }}
                  className="flex items-center gap-2 rounded-xl border border-border bg-background px-5 py-2.5 font-body text-sm font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-accent/20"
                >
                  <span className="text-base">👎</span>
                  <span>Not quite</span>
                </motion.button>
              </motion.div>
            ) : (
              <motion.p
                key="thanks"
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                className="font-body text-xs text-muted-foreground"
              >
                {submitted
                  ? 'Thank you for your feedback. ✨'
                  : voted === 'yes'
                  ? 'Thank you — glad it helped! ✨'
                  : "Thank you for letting us know. We'll keep improving."}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        {/* Divider */}
        <div className="h-px w-full bg-border" />

        {/* Philosophy line */}
        <p
          className="font-body text-[11px] leading-relaxed italic"
          style={{ color: 'hsl(38 35% 52%)' }}
        >
          Skill builds great stylists.<br />
          Kindness builds legendary careers.
        </p>
      </motion.div>
    </>
  );
}
